import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { AlertCircle, RefreshCw, Activity } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

/**
 * MM audit 3.4: Bounce detector UI.
 *
 * Показывает recent bounce-события (жетоны, вернувшиеся из-за failed swap'а)
 * + статистику сканера. Автообновление 15s.
 */
const fmt = (v) => (v == null ? '—' : v);

const BounceCard = () => {
  const [stats, setStats] = useState(null);
  const [bounces, setBounces] = useState([]);

  const load = async () => {
    try {
      const s = await fetch(`${API_URL}/api/market-maker/bounces/stats`).then(r => r.json());
      setStats(s);
      const b = await fetch(`${API_URL}/api/market-maker/bounces/recent?limit=15`).then(r => r.json());
      setBounces(b.bounces || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const running = !!stats?.running;
  const total = stats?.bounces_detected_total || 0;
  const interval = stats?.config?.poll_interval_seconds || '?';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> Bounce Detector
          {running ? (
            <Badge className="bg-emerald-600 text-xs">
              <Activity className="h-3 w-3 mr-1" />
              scanning every {interval}s
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs">stopped</Badge>
          )}
          {total > 0 && (
            <Badge className="bg-yellow-600 text-xs">
              <AlertCircle className="h-3 w-3 mr-1" />
              {total} total
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          Ищет bounce-события на managed wallets (возвращённые jetton/TON после failed swap).
          При детекции — forced reconcile.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
          <div>
            <div className="text-xs text-slate-400">Ticks</div>
            <div className="font-bold">{fmt(stats?.ticks)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Wallets scanned</div>
            <div className="font-bold">{fmt(stats?.wallets_scanned_total)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Bounces total</div>
            <div className={`font-bold ${total > 0 ? 'text-yellow-400' : ''}`}>{total}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Last tick</div>
            <div className="font-bold text-xs">
              {stats?.last_tick_duration_sec || 0}s
              {stats?.last_tick_at && (
                <span className="text-slate-500 ml-1">
                  @ {(stats.last_tick_at).slice(11, 19)}
                </span>
              )}
            </div>
          </div>
        </div>

        {bounces.length > 0 ? (
          <details open>
            <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">
              Recent bounces ({bounces.length})
            </summary>
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left">Detected</th>
                  <th>Wallet</th>
                  <th>Type</th>
                  <th>Tx</th>
                </tr>
              </thead>
              <tbody>
                {bounces.map((b, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="text-slate-400">{(b.detected_at || '').slice(0, 19)}</td>
                    <td className="font-mono">{(b.wallet_address || '').slice(0, 12)}...</td>
                    <td className="text-yellow-400">{b.action_type}</td>
                    <td className="font-mono text-slate-500">
                      {b.tx_hash ? (
                        <a
                          href={`https://tonviewer.com/transaction/${b.tx_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-slate-200"
                        >
                          {b.tx_hash.slice(0, 12)}...
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        ) : (
          <div className="text-xs text-slate-500">No bounces detected yet.</div>
        )}

        {stats?.last_error && (
          <div className="mt-2 text-xs text-red-400">
            Last error: {stats.last_error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BounceCard;
